class IPSGoalFinder extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-ips-finder-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-ips-finder-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const tabIndex = this.tabs.findIndex((tab) => tab.getAttribute('data-shopify-editor-block') === event.detail.blockId);
        if (tabIndex >= 0) this.activate(tabIndex);
      });
    }
  }

  activate(index, focus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
    });
  }

  handleKeydown(event, index) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex = index;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === index) return;

    event.preventDefault();
    this.activate(nextIndex, true);
  }
}

if (!customElements.get('ips-goal-finder')) {
  customElements.define('ips-goal-finder', IPSGoalFinder);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-ips-scroll]');
  if (!button) return;

  const target = document.getElementById(button.dataset.ipsScrollTarget);
  if (!target) return;

  const direction = button.dataset.ipsScroll === 'previous' ? -1 : 1;
  const amount = Math.max(target.clientWidth * 0.82, 320);
  target.scrollBy({ left: amount * direction, behavior: 'smooth' });
});


const getAppliedDiscountCodes = (root = document) =>
  Array.from(root.querySelectorAll('[data-ips-applied-code]'))
    .map((element) => element.dataset.ipsAppliedCode?.trim())
    .filter(Boolean);

const getCartDiscountTitles = (cart) => {
  const titles = new Set();

  cart?.cart_level_discount_applications?.forEach((discount) => {
    if (discount?.title) titles.add(discount.title.toUpperCase());
  });

  cart?.items?.forEach((item) => {
    item?.discounts?.forEach((discount) => {
      if (discount?.title) titles.add(discount.title.toUpperCase());
    });
    item?.line_level_discount_allocations?.forEach((allocation) => {
      const title = allocation?.discount_application?.title;
      if (title) titles.add(title.toUpperCase());
    });
  });

  return titles;
};

const getCartPageSectionIds = () => {
  const itemsSectionId = document
    .querySelector('cart-items')
    ?.closest('.shopify-section')
    ?.id?.replace('shopify-section-', '');
  const footerSectionId = document.querySelector('#main-cart-footer')?.dataset.id;

  return [...new Set([itemsSectionId, footerSectionId].filter(Boolean))];
};

const replaceCartSection = (parser, sectionId, html) => {
  if (!sectionId || !html) return;

  const sectionDocument = parser.parseFromString(html, 'text/html');
  const currentSection = document.getElementById(`shopify-section-${sectionId}`);
  const nextSection = sectionDocument.getElementById(`shopify-section-${sectionId}`);

  if (currentSection && nextSection) currentSection.innerHTML = nextSection.innerHTML;
};

const renderCartDiscountUpdate = (cart, feedback = {}) => {
  const parser = new DOMParser();
  const cartPageSectionIds = getCartPageSectionIds();
  const isCartPage = cartPageSectionIds.length > 0;

  if (isCartPage) {
    cartPageSectionIds.forEach((sectionId) => {
      replaceCartSection(parser, sectionId, cart?.sections?.[sectionId]);
    });
  }

  if (cart?.sections?.['cart-icon-bubble']) {
    const iconDocument = parser.parseFromString(cart.sections['cart-icon-bubble'], 'text/html');
    const nextIcon = iconDocument.querySelector('#cart-icon-bubble');
    const currentIcon = document.querySelector('#cart-icon-bubble');
    if (nextIcon && currentIcon) currentIcon.innerHTML = nextIcon.innerHTML;
  }

  const drawer = document.querySelector('cart-drawer');
  if (!isCartPage) {
    if (!drawer || !cart?.sections?.['cart-drawer']) return;

    const drawerDocument = parser.parseFromString(cart.sections['cart-drawer'], 'text/html');
    const nextDrawer = drawerDocument.querySelector('#CartDrawer');
    const currentDrawer = drawer.querySelector('#CartDrawer');

    if (nextDrawer && currentDrawer) currentDrawer.innerHTML = nextDrawer.innerHTML;

    drawer.classList.toggle('is-empty', cart.item_count === 0);
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
  }

  const feedbackRoot = isCartPage ? document.querySelector('#main-cart-footer') : drawer;
  const panel = feedbackRoot?.querySelector('[data-ips-discount-panel]');
  const input = panel?.querySelector('input[name="discount"]');
  const error = panel?.querySelector('[data-ips-discount-error]');
  const status = feedbackRoot?.querySelector('[data-ips-cart-feedback]');

  if (feedback.error) {
    if (panel) panel.open = true;
    if (input) input.value = feedback.value || '';
    if (error) {
      error.textContent = feedback.error;
      error.hidden = false;
    }
    input?.focus();
    return;
  }

  if (panel) panel.open = false;
  if (status && feedback.message) {
    status.textContent = feedback.message;
    status.hidden = false;
    window.setTimeout(() => {
      if (status.isConnected) status.hidden = true;
    }, 4200);
  }
};

const updateCartDiscounts = async (codes) => {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      discount: codes.join(','),
      sections: ['cart-drawer', 'cart-icon-bubble', ...getCartPageSectionIds()],
      sections_url: window.location.pathname + window.location.search,
    }),
  });

  const cart = await response.json();
  if (!response.ok) throw new Error(cart?.description || cart?.message || 'Unable to update the discount.');
  return cart;
};

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ips-discount-form]');
  if (!form) return;

  event.preventDefault();
  const input = form.querySelector('input[name="discount"]');
  const error = form.closest('[data-ips-discount-panel]')?.querySelector('[data-ips-discount-error]');
  const button = form.querySelector('button[type="submit"]');
  const code = input?.value.trim().toUpperCase();

  if (!code) {
    if (error) {
      error.textContent = 'Enter a discount code.';
      error.hidden = false;
    }
    input?.focus();
    return;
  }

  if (error) error.hidden = true;
  button?.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;

  try {
    const existingCodes = getAppliedDiscountCodes(document);
    const codes = [...new Set([...existingCodes, code])];
    const cart = await updateCartDiscounts(codes);
    const applied = getCartDiscountTitles(cart).has(code);

    renderCartDiscountUpdate(
      cart,
      applied
        ? { message: `${code} applied. Your total has been updated.` }
        : { error: 'That code is not valid for the items in this cart.', value: code },
    );
  } catch (requestError) {
    if (error) {
      error.textContent = requestError.message || 'We could not apply that code. Please try again.';
      error.hidden = false;
    }
    if (button) button.disabled = false;
    button?.removeAttribute('aria-busy');
  }
});

document.addEventListener('click', async (event) => {
  const removeButton = event.target.closest('[data-ips-remove-discount]');
  if (!removeButton) return;

  event.preventDefault();
  const code = removeButton.dataset.ipsRemoveDiscount;
  const drawer = removeButton.closest('cart-drawer');
  const discountScope = drawer || document;
  const remainingCodes = getAppliedDiscountCodes(discountScope).filter(
    (appliedCode) => appliedCode.toUpperCase() !== code.toUpperCase(),
  );

  removeButton.disabled = true;
  removeButton.setAttribute('aria-busy', 'true');

  try {
    const cart = await updateCartDiscounts(remainingCodes);
    renderCartDiscountUpdate(cart, { message: `${code} removed. Your total has been updated.` });
  } catch (requestError) {
    removeButton.disabled = false;
    removeButton.removeAttribute('aria-busy');
    const status = (drawer || document.querySelector('#main-cart-footer'))?.querySelector('[data-ips-cart-feedback]');
    if (status) {
      status.textContent = requestError.message || 'We could not remove that code. Please try again.';
      status.hidden = false;
    }
  }
});

(() => {
  const isCartPage = window.location.pathname === '/cart';
  if (isCartPage) return;

  const setupHomepageCartToast = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.ipsHomepageToastReady === 'true') return;

    drawer.dataset.ipsHomepageToastReady = 'true';

    const toast = document.createElement('div');
    toast.className = 'ips-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    toast.innerHTML = `
      <span class="ips-cart-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false"><path d="M6.5 12.5 10 16l7.5-8"/></svg>
      </span>
      <span class="ips-cart-toast__copy">
        <strong>Added to your cart</strong>
        <span data-ips-cart-toast-product>Product added successfully</span>
      </span>
      <a class="ips-cart-toast__link" href="/cart">View cart</a>
    `;
    // Keep confirmation out of the accessibility tree until an add succeeds. Arqum 2026-10-05.
    toast.hidden = true;
    document.body.appendChild(toast);

    const productName = toast.querySelector('[data-ips-cart-toast-product]');
    let dismissTimer;

    const showToast = (state) => {
      const name = state?.product_title || state?.title || 'Product added successfully';
      productName.textContent = name === 'Default Title' ? 'Product added successfully' : name;

      window.clearTimeout(dismissTimer);
      toast.hidden = false;
      toast.classList.remove('is-visible');
      toast.getBoundingClientRect();
      toast.classList.add('is-visible');
      dismissTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        toast.hidden = true;
      }, 3600);
    };

    // Confirm only after the shared drawer and cart count refresh successfully. Arqum 2026-10-05.
    drawer.renderContents = async function renderHomepageCartContents(parsedState) {
      await this.refreshContents(parsedState);
      showToast(parsedState);
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupHomepageCartToast, { once: true });
  } else {
    setupHomepageCartToast();
  }

  document.addEventListener('shopify:section:load', setupHomepageCartToast);
})();

/* IPS header motion enhancement - v1.8 */
(() => {
  const setupScrollHeader = () => {
    const header = document.querySelector('.section-header');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    if (!header || header.dataset.ipsScrollHeaderReady === 'true') return;

    header.dataset.ipsScrollHeaderReady = 'true';
    header.dataset.ipsScrollHeader = '';

    if (window.Shopify?.designMode || reduceMotion?.matches) return;

    let lastScrollY = window.scrollY;
    let downDistance = 0;
    let upDistance = 0;
    let ticking = false;

    const showHeader = () => header.classList.remove('ips-header--hidden');
    const updateHeader = () => {
      const currentScrollY = window.scrollY;
      const distance = currentScrollY - lastScrollY;

      if (currentScrollY < 96) {
        downDistance = 0;
        upDistance = 0;
        showHeader();
      } else if (distance > 0) {
        downDistance += distance;
        upDistance = 0;

        if (downDistance >= 36) header.classList.add('ips-header--hidden');
      } else if (distance < 0) {
        upDistance += Math.abs(distance);
        downDistance = 0;

        if (upDistance >= 12) showHeader();
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      },
      { passive: true },
    );

    header.addEventListener('focusin', showHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupScrollHeader, { once: true });
  } else {
    setupScrollHeader();
  }

  document.addEventListener('shopify:section:load', setupScrollHeader);
})();


/* Update the IPS cart summary from the same successful cart response. */
document.addEventListener('ips:cart-updated', (event) => {
  const footer = document.getElementById('main-cart-footer');
  const sectionId = footer?.dataset.id;
  const sectionMarkup = event.detail?.sections?.[sectionId];
  const currentSummary = footer?.querySelector('.ips-cart-bottom');

  if (!sectionMarkup || !currentSummary) return;

  const nextDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
  const nextSummary = nextDocument.querySelector('.ips-cart-bottom');

  if (nextSummary) currentSummary.replaceWith(nextSummary);
});


/* Update the free-delivery monitor from the same successful cart response. */
document.addEventListener('ips:cart-updated', (event) => {
  const cartItems = document.querySelector('cart-items');
  const sectionId = cartItems?.querySelector('#main-cart-items')?.dataset.id;
  const sectionMarkup = event.detail?.sections?.[sectionId];
  const currentProgress = cartItems?.querySelector('.ips-cart-progress');

  // Clear stale progress when the last item is removed. Arqum 2026-10-04.
  if (event.detail?.item_count === 0) {
    currentProgress?.remove();
    return;
  }

  if (!sectionMarkup || !currentProgress) return;

  const nextDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
  const nextProgress = nextDocument.querySelector('.ips-cart-progress');

  if (nextProgress) currentProgress.replaceWith(nextProgress);
});
